import React, { useState, useRef, useEffect } from "react";
import { Form, Input, Select, Upload, Row, Col, message, Image, Space, Button, Typography, UploadFile, Radio } from "antd";
import { UploadOutlined, SignatureOutlined } from "@ant-design/icons";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import styles from "./CompanyDetails.module.css";
import { CommonService } from "../../../../services/Common/CommonService";
import { DropdownsResponse } from "../../../../models/ICommon";
import { CommonDropdownParams } from "../../../../constants/CommonDropdownParams";
import CountryDropdownItem from "../../../Countries/CountryDropdownItem";
import { CreatorProfileData, CreatorProfileService } from "../../../../services/Invoices/CreatorProfile/CreatorProfileService";
import { mapBackendToCustomFields, mapCustomFieldsToJson } from "../../../comman/CustomFields/CustomFieldMapper";
import CustomFields, { CustomField } from "../../../comman/CustomFields/CustomFields";
import { GlobalButton } from "../../../comman/GlobalButton.styles";
import LabelWithTooltip from "../../../comman/LabelWithTooltip.tsx/LabelWithTooltip";
import { profileFormConfig } from "./profileFormConfig";
import { createCharacterValidatorRule } from "../../../../validators/inputCharacterValidator";
import DigitalSignature from "./DigitalSignature";
import { formatPhone } from "../../../../utils/formatPhone";

const { Option } = Select;
const { Text } = Typography;

interface CompanyDetailsFormProps {
	initialValues?: Partial<CreatorProfileData>;
	onSave: (values: FormValues) => void;
	loading?: boolean;
	userId: string;
}

export interface FormValues {
	individualName: string;
	businessName: string;
	businessTypeId: number;
	genderId?: number;
	occupationId?: number;
	websiteLink?: string;
	taxId: string;
	phoneNumber: string;
	businessEmail: string;
	postalAddressLine: string;
	townCity: string;
	provinceState: string;
	postalCode: string;
	countryId: number;
	taxDocument: File;
	logoImage: File;
	digitalSignImage: File;
	logo: UploadFile[];
	taxDoc: UploadFile[];
	additionalDataJson: string;
	isFreelancer: boolean;
	registerReasonId?: number;
	registerReasonOther?: string;
}

const CompanyDetails: React.FC<CompanyDetailsFormProps> = ({ initialValues, onSave, loading, userId }) => {
	const [form] = Form.useForm<FormValues>();
	const [pageLoading, setPageLoading] = useState(false);
	const [isFreelancer, setIsFreelancer] = useState<boolean | null>(null);
	const [customFields, setCustomFields] = useState<CustomField[]>([]);
	const [signature, setSignature] = useState<string>("");
	const [signatureFile, setSignatureFile] = useState<File | null>(null);
	const [logoPreview, setLogoPreview] = useState<string | null>(null);
	const [dropdowns, setDropdowns] = useState<DropdownsResponse>({});
	const profileType = isFreelancer ? "freelancer" : "business";
	const config = profileFormConfig[profileType];
	const registerReasonId = Form.useWatch("registerReasonId", form);

	useEffect(() => {
		getCommonDropdown([
			CommonDropdownParams.CountryFromCountryMaster,
			CommonDropdownParams.BusinessType,
			CommonDropdownParams.RegisterReason,
			CommonDropdownParams.Gender,
			CommonDropdownParams.Occupation
		]);
	}, []);

	useEffect(() => {
		if (initialValues) {
			setLogoPreview(initialValues.logoUrl);
			if (initialValues.hasDigitalSignature) {
				handlePreview("sign");
			}
			if (initialValues.hasTaxDocument) {
				handlePreview("taxproof");
			}
			const fields = mapBackendToCustomFields(initialValues?.additionalFieldData);
			if (fields) setCustomFields(fields);
			form.setFieldsValue(initialValues);
			setIsFreelancer(initialValues?.isFreelancer);
		}
	}, [initialValues, form]);

	const getCommonDropdown = (ids: number[]) => {
		setPageLoading(true);
		CommonService.getAllDropdowns(ids)
			.then((rawData) => {
				setDropdowns(rawData);
			})
			.catch((error) => {
				message.error("Failed to load dropdowns");
			})
			.finally(() => setPageLoading(false));
	};

	const handlePreview = async (type: string) => {
		try {
			const { file, url } = await CreatorProfileService.getDocument({
				userId: Number(userId),
				docType: type
			});
			if (type === "sign") {
				setSignature(url);
				fetch(url)
					.then((res) => res.blob())
					.then((blob) => {
						const file = new File([blob], "digital-signature.png", { type: blob.type });
						setSignatureFile(file);
					});
			}
			if (type === "taxproof") {
				const fileList: UploadFile[] = [
					{
						uid: "-1",
						name: file.name,
						status: "done",
						url: url
					}
				];
				form.setFieldValue("taxDoc", fileList);
			}
		} catch (err) {
			message.error("Failed to load document preview");
		}
	};

	const logoUploadProps = {
		beforeUpload: (file: File) => {
			const isAllowedType = file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg";

			if (!isAllowedType) {
				message.error("Only JPG, JPEG and PNG  files are allowed.");
				return Upload.LIST_IGNORE;
			}

			const isLt1MB = file.size / 1024 / 1024 < 1;
			if (!isLt1MB) {
				message.error("Logo must be smaller than 1MB.");
				return Upload.LIST_IGNORE;
			}
			return new Promise<boolean>((resolve) => {
				const img = document.createElement("img");

				img.src = URL.createObjectURL(file);

				img.onload = () => {
					const { width, height } = img;

					if (width < 200 || height < 200 || width > 1000 || height > 1000) {
						message.error("Logo size must be between 200×200 and 1000×1000 pixels.");
						resolve(Upload.LIST_IGNORE as any);
					} else {
						resolve(false);
					}
				};
			});

			return false;
		},

		maxCount: 1,
		onChange: (info: any) => {
			if (info.fileList.length > 0) {
				const file = info.fileList[0].originFileObj;
				const reader = new FileReader();
				reader.onload = (e) => setLogoPreview(e.target?.result as string);
				reader.readAsDataURL(file);
			} else {
				setLogoPreview(null);
			}
		},
		onRemove: () => {
			setLogoPreview(null);
			return true;
		}
	};

	const onFinish = (values: FormValues) => {
		if (!signatureFile) {
			message.error("Please draw and save your digital signature");
			return;
		}
		const logoFile = values.logo?.[0]?.originFileObj as File;
		const taxFile = values.taxDoc?.[0]?.originFileObj as File;

		const finalValues = {
			...values,
			businessEmail: isFreelancer ?  initialValues.businessEmail: values.businessEmail,
			businessTypeId: isFreelancer ? initialValues.businessTypeId : values.businessTypeId,
			phoneNumber: formatPhone(values.phoneNumber),
			logoImage: isFreelancer ? signatureFile : logoFile,
			taxDocument: taxFile,
			digitalSignImage: signatureFile,
			additionalDataJson: mapCustomFieldsToJson(customFields),
			isFreelancer: isFreelancer
		};
		onSave(finalValues);
	};
	const taxUploadProps = { beforeUpload: () => false, maxCount: 1 };
	return (
		<Form form={form} layout="vertical" onFinish={onFinish} initialValues={initialValues}>
			<div className="text-center my-4">
				<Text className="mx-2">
					<span className="fw-bold">Select your profile type :</span> Freelancer for individual work or Business for a registered company.{" "}
				</Text>
				<Radio.Group value={isFreelancer} onChange={(e) => setIsFreelancer(e.target.value)}>
					<Radio value={true}>Freelancer</Radio>
					<Radio value={false}>Business</Radio>
				</Radio.Group>
			</div>
			{isFreelancer != null && (
				<>
					<Row gutter={[24, 24]}>
						<Col xs={24} md={12}>
							<Form.Item
								name="individualName"
								label={<LabelWithTooltip label={config.individualName.label} tooltip={config.individualName.tooltip} />}
								rules={[{ required: true, message: config.individualName.requiredMessage }, createCharacterValidatorRule()]}
							>
								<Input placeholder={config.individualName.placeholder} />
							</Form.Item>
						</Col>

						<Col xs={24} md={12}>
							<Form.Item
								name="businessName"
								label={<LabelWithTooltip label={config.businessName.label} tooltip={config.businessName.tooltip} />}
								rules={[{ required: true, message: config.businessName.requiredMessage }, createCharacterValidatorRule()]}
							>
								<Input placeholder={config.businessName.placeholder} />
							</Form.Item>
						</Col>

						{!isFreelancer && (
							<Col xs={24} md={12}>
								<Form.Item
									name="businessTypeId"
									label={<LabelWithTooltip label={config.businessType.label} tooltip={config.businessType.tooltip} />}
									rules={[{ required: true, message: config.businessType.requiredMessage }]}
								>
									<Select
										placeholder={config.businessType.placeholder}
										showSearch
										filterOption={(input, option) =>
											(option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
										}
									>
										{dropdowns?.BusinessType?.map((type) => (
											<Option key={type.id} value={type.id}>
												{type.label}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						)}

						<Col xs={24} md={12}>
							<Form.Item
								name="websiteLink"
								label={<LabelWithTooltip label={config.website.label} tooltip={config.website.tooltip} />}
								rules={[
									{
										validator: (_, value) => {
											if (!value) {
												return Promise.resolve();
											}

											if (!/^https?:\/\//i.test(value.trim())) {
												return Promise.reject(config.website.urlError);
											}
											return Promise.resolve();
										}
									}
								]}
							>
								<Input placeholder={config.website.placeholder} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name="taxId"
								label={<LabelWithTooltip label={config.taxId.label} tooltip={config.taxId.tooltip} />}
								rules={[{ required: true, message: config.taxId.requiredMessage }, createCharacterValidatorRule()]}
							>
								<Input placeholder={config.taxId.placeholder} />
							</Form.Item>
						</Col>

						<Col xs={24} md={12}>
							<Form.Item
								name="phoneNumber"
								label={<LabelWithTooltip label={config.phone.label} tooltip={config.phone.tooltip} />}
								rules={[
									{ required: true, message: config.phone.requiredMessage },
									{
										validator: (_, value) => {
											if (!value) return Promise.resolve();
											return isValidPhoneNumber(value) ? Promise.resolve() : Promise.reject("Invalid phone number");
										}
									}
								]}
							>
								<PhoneInput
									className="form-control Phone-input"
									type="text"
									international={false}
									withCountryCallingCode={true}
									countryCallingCodeEditable={false}
									placeholder={config.phone.placeholder}
									onChange={(v) => form.setFieldsValue({ phoneNumber: v })}
								/>
							</Form.Item>
						</Col>
						{isFreelancer && (
							<Col xs={24} md={12}>
								<Form.Item
									name="genderId"
									label={<LabelWithTooltip label={config.gender.label} tooltip={config.gender.tooltip} />}
									rules={[{ required: true, message: config.gender.requiredMessage }]}
								>
									<Select
										placeholder={config.gender.placeholder}
										showSearch
										filterOption={(input, option) =>
											(option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
										}
									>
										{dropdowns?.Gender?.map((type) => (
											<Option key={type.id} value={type.id}>
												{type.label}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						)}
						{isFreelancer && (
							<Col xs={24} md={12}>
								<Form.Item
									name="occupationId"
									label={<LabelWithTooltip label={config.occupation.label} tooltip={config.occupation.tooltip} />}
									rules={[{ required: true, message: config.occupation.requiredMessage }]}
								>
									<Select
										placeholder={config.occupation.placeholder}
										showSearch
										filterOption={(input, option) =>
											(option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
										}
									>
										{dropdowns?.Occupation?.map((type) => (
											<Option key={type.id} value={type.id}>
												{type.label}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						)}
						{!isFreelancer && (
							<Col xs={24} md={12}>
								<Form.Item
									name="businessEmail"
									label={<LabelWithTooltip label={config.email.label} tooltip={config.email.tooltip} />}
									rules={[
										{ required: true, message: config.email.requiredMessage },
										{ type: "email", message: config.email.invalidMessage },
										{ max: 320, message: "Email is too long." }
									]}
								>
									<Input placeholder={config.email.placeholder} />
								</Form.Item>
							</Col>
						)}
						<Col xs={24} md={12}>
							<Form.Item
								name="postalAddressLine"
								label={<LabelWithTooltip label={config.addressLine.label} tooltip={config.addressLine.tooltip} />}
								rules={[{ required: true, message: config.addressLine.requiredMessage }, createCharacterValidatorRule()]}
							>
								<Input placeholder={config.addressLine.placeholder} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name="townCity"
								label={<LabelWithTooltip label={config.city.label} tooltip={config.city.tooltip} />}
								rules={[{ required: true, message: config.city.requiredMessage }, createCharacterValidatorRule()]}
							>
								<Input placeholder={config.city.placeholder} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name="provinceState"
								label={<LabelWithTooltip label={config.state.label} tooltip={config.state.tooltip} />}
								rules={[{ required: true, message: config.state.requiredMessage }, createCharacterValidatorRule()]}
							>
								<Input placeholder={config.state.placeholder} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name="postalCode"
								label={<LabelWithTooltip label={config.postalCode.label} tooltip={config.postalCode.tooltip} />}
								rules={[{ required: true, message: config.postalCode.requiredMessage }, createCharacterValidatorRule()]}
							>
								<Input placeholder={config.postalCode.placeholder} />
							</Form.Item>
						</Col>

						<Col xs={24} md={12}>
							<Form.Item
								name="countryId"
								label={<LabelWithTooltip label={config.country.label} tooltip={config.country.tooltip} />}
								rules={[{ required: true, message: config.country.requiredMessage }]}
							>
								<Select
									placeholder="Select country"
									showSearch
									optionFilterProp="searchText"
									filterSort={(optionA, optionB) =>
										(optionA.searchText as string)?.toLowerCase().localeCompare((optionB.searchText as string)?.toLowerCase())
									}
									options={dropdowns?.CountriesFromCountryMaster?.map((item) => ({
										value: item.id,
										label: <CountryDropdownItem countryName={item.label} />,
										searchText: item.label
									}))}
								></Select>
							</Form.Item>
						</Col>

						<Col xs={24} md={12}>
							<Form.Item
								name="registerReasonId"
								label="Reason for Registration"
								rules={[{ required: true, message: "Please enter your reason of registration" }]}
							>
								<Select placeholder="Select reason">
									{dropdowns?.RegisterReason?.map((reason) => (
										<Option key={reason.id} value={reason.id}>
											{reason.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>

						{registerReasonId === 7 && (
							<Col xs={24} md={12}>
								<Form.Item
									name="registerReasonOther"
									label="Other (please specify)"
									rules={[{ required: true, message: "Please provide details" }]}
								>
									<Input placeholder="Explain your reason..." />
								</Form.Item>
							</Col>
						)}
					</Row>
					<Row gutter={[24, 24]}>
						<Col xs={24} md={12}>
							<Form.Item
								name="taxDoc"
								label={<LabelWithTooltip label={config.taxDocument.label} tooltip={config.taxDocument.tooltip} />}
								valuePropName="fileList"
								getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
								rules={[
									{
										required: true,
										message: config.taxDocument.requiredMessage
									}
								]}
							>
								<Upload {...taxUploadProps} accept=".pdf,.jpg,.jpeg,.png">
									<Button icon={<UploadOutlined />}>Upload Document</Button>
								</Upload>
							</Form.Item>
						</Col>
						{!isFreelancer && (
							<>
								{" "}
								<Col xs={24} md={8}>
									<Form.Item
										name="logo"
										label={<LabelWithTooltip label={config.logo.label} tooltip={config.logo.tooltip} />}
										valuePropName="fileList"
										getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
										rules={[{ required: !logoPreview, message: config.logo.requiredMessage }]}
									>
										<Upload {...logoUploadProps} accept="image/*">
											<Button icon={<UploadOutlined />}>Upload Logo</Button>
										</Upload>
									</Form.Item>
								</Col>
								<Col xs={24} md={4} style={{ display: "flex", alignItems: "center" }}>
									{logoPreview && (
										<Image
											src={logoPreview}
											alt="Logo Preview"
											width={120}
											style={{ objectFit: "contain", border: "1px solid #ddd", borderRadius: "8px" }}
										/>
									)}
								</Col>
							</>
						)}

						<Col xs={24} md={12}>
							<Form.Item
								label={<LabelWithTooltip label={config.digitalSignature.label} tooltip={config.digitalSignature.tooltip} />}
								required
							>
								<DigitalSignature
									initialSignatureUrl={signature}
									hideDraw={isFreelancer}
									defaultTypedName={form.getFieldValue("individualName") || form.getFieldValue("businessName") || ""}
									onSave={(file) => {
										setSignatureFile(file);
										message.success("Signature attached");
									}}
									onClear={() => {
										setSignature("");
										setSignatureFile(null);
									}}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Custom Fields">
								<CustomFields customFields={customFields} setCustomFields={setCustomFields} />
							</Form.Item>
						</Col>
					</Row>
					<div className="d-flex justify-content-center mt-5">
						<GlobalButton className="primary" htmlType="submit" loading={loading} size="large">
							{initialValues ? "Next" : "Save Company Details"}
						</GlobalButton>
					</div>
				</>
			)}
		</Form>
	);
};

export default CompanyDetails;
